import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAnimationFrame } from '@/hooks/useAnimationFrame';

type Range = { start: number; end: number };

export interface MiniSeekBarProps {
  /** The audio element it scrubs. */
  videoEl: HTMLMediaElement;
  /**
   * Audio peak envelope (0..1 per column) drawn behind the playhead. Pass `null` while it's loading / when the file has no
   * audio: the bar is laid out the same, so nothing jumps when it lands.
   */
  peaks: Float32Array | null;
  /** 0..1 of the timeline `peaks` covers so far (a waveform still filling in from the left). Default 1. */
  peaksCoverage?: number;
  /** Text drawn inside the bar at the right (the time). */
  trailing?: string;
}

/** The bar's track height, in px. */
const TRACK_H = 14;
/** The waveform never fills the full track height, so the loudest peak still reads as a peak. */
const WAVE_FILL = 0.92;
/**
 * Scrub seeks are rate-limited to this: every `currentTime` write aborts the previous seek and starts a new range fetch (on the
 * encrypted stream a fresh part decrypt per write), so a 60 Hz drag would flood it. The release always lands the exact place.
 */
const SCRUB_SEEK_INTERVAL_MS = 80;

/**
 * Draw `peaks` as a vertically mirrored envelope polygon across the canvas, the loudest peak under each pixel column, so it stays
 * honest both squeezed (many peaks per pixel) and stretched.
 */
function drawPeaks(canvas: HTMLCanvasElement, peaks: Float32Array, width: number, height: number, color: string): void {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);
  const n = peaks.length;
  const cols = Math.floor(width);
  if (n === 0 || cols <= 0 || height <= 0) return;
  const mid = height / 2;
  const perCol = n / cols;
  const column = new Float32Array(cols);
  for (let x = 0; x < cols; x++) {
    const a = x * perCol;
    let i0 = Math.max(0, Math.floor(a));
    if (i0 >= n) continue;
    const i1 = Math.min(n - 1, Math.max(i0, Math.ceil(a + perCol) - 1));
    let peak = 0;
    for (; i0 <= i1; i0++) if (peaks[i0] > peak) peak = peaks[i0];
    column[x] = peak;
  }
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, mid);
  for (let x = 0; x < cols; x++) ctx.lineTo(x + 0.5, mid - (column[x] > 0 ? Math.max(0.5, column[x] * mid * WAVE_FILL) : 0));
  ctx.lineTo(width, mid);
  for (let x = cols - 1; x >= 0; x--) ctx.lineTo(x + 0.5, mid + (column[x] > 0 ? Math.max(0.5, column[x] * mid * WAVE_FILL) : 0));
  ctx.closePath();
  ctx.fill();
}

/**
 * The compact waveform scrubber of an audio element (WaveformAudioPlayer: a chat's audio messages and the public storefront, which
 * have no sleepy-ui). The sleepy-ui surfaces (the Library's preview, a texture slot's card) draw their seek bar natively
 * (VIDOS kit/SeekStrip.h, filmstrip and waveform); this DOM one is only for the pages that draw no module. Playback position is
 * painted imperatively every frame (no React re-renders), buffered coverage at network pace.
 */
export const MiniSeekBar: React.FC<MiniSeekBarProps> = ({ videoEl, peaks, peaksCoverage = 1, trailing }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const waveBaseRef = useRef<HTMLCanvasElement>(null);
  const waveBrightRef = useRef<HTMLCanvasElement>(null);
  const [dragging, setDragging] = useState(false);
  const [buffered, setBuffered] = useState<Range[]>([]);
  const [trackWidth, setTrackWidth] = useState(0);
  const wasPlayingRef = useRef(false);

  const durationOf = useCallback(() => (Number.isFinite(videoEl.duration) ? videoEl.duration : 0), [videoEl]);

  // The playhead, the played wash and the bright half of the waveform follow the element every frame.
  useAnimationFrame(() => {
    const duration = durationOf();
    if (!duration) return;
    const pct = Math.max(0, Math.min(100, (videoEl.currentTime / duration) * 100));
    if (fillRef.current) fillRef.current.style.width = `${pct}%`;
    if (waveBrightRef.current) waveBrightRef.current.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    if (playheadRef.current) playheadRef.current.style.left = `${pct}%`;
  }, true);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const measure = () => setTrackWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-raster the waveform only when the peaks or the width change. Only the covered prefix is real data.
  useEffect(() => {
    if (!peaks || trackWidth <= 0) return;
    const n = Math.max(0, Math.min(peaks.length, Math.round(peaks.length * Math.max(0, Math.min(1, peaksCoverage)))));
    const covered = n < peaks.length ? peaks.subarray(0, n) : peaks;
    // A covered prefix is stretched over the whole bar's share of it.
    const shown = n < peaks.length ? new Float32Array(peaks.length).fill(0) : covered;
    if (n < peaks.length) shown.set(covered);
    if (waveBaseRef.current) drawPeaks(waveBaseRef.current, shown, trackWidth, TRACK_H, 'rgba(255,255,255,0.32)');
    if (waveBrightRef.current) drawPeaks(waveBrightRef.current, shown, trackWidth, TRACK_H, 'rgba(255,255,255,0.85)');
  }, [peaks, peaksCoverage, trackWidth]);

  // Buffered spans change at network pace: throttled state, coalesced to one update per frame.
  useEffect(() => {
    let raf = 0;
    let lastSig = '';
    const sync = () => {
      const d = videoEl.duration;
      const ranges: Range[] = [];
      if (d && Number.isFinite(d)) {
        for (let i = 0; i < videoEl.buffered.length; i++) ranges.push({ start: videoEl.buffered.start(i), end: videoEl.buffered.end(i) });
      }
      const sig = ranges.map((r) => `${r.start.toFixed(2)}-${r.end.toFixed(2)}`).join(',');
      if (sig !== lastSig) { lastSig = sig; setBuffered(ranges); }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; sync(); }); };
    const types = ['progress', 'timeupdate', 'seeked', 'loadedmetadata'];
    types.forEach((type) => videoEl.addEventListener(type, schedule));
    sync();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      types.forEach((type) => videoEl.removeEventListener(type, schedule));
    };
  }, [videoEl]);

  const seek = useCallback((clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    const duration = durationOf();
    if (!rect || rect.width <= 0 || !duration) return;
    videoEl.currentTime = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * duration;
  }, [videoEl, durationOf]);

  // A drag seeks at most every SCRUB_SEEK_INTERVAL_MS; the newest place waits its turn, and the release lands the exact one.
  const pendingRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAtRef = useRef(0);
  const scrubSeek = useCallback((clientX: number) => {
    const now = Date.now();
    if (now - lastAtRef.current >= SCRUB_SEEK_INTERVAL_MS && !timerRef.current) {
      lastAtRef.current = now;
      seek(clientX);
      return;
    }
    pendingRef.current = clientX;
    if (!timerRef.current) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const x = pendingRef.current;
        pendingRef.current = null;
        if (x != null) { lastAtRef.current = Date.now(); seek(x); }
      }, Math.max(0, SCRUB_SEEK_INTERVAL_MS - (now - lastAtRef.current)));
    }
  }, [seek]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    // Scrubbing pauses so the place under the pointer sounds clean; it resumes on release.
    wasPlayingRef.current = !videoEl.paused;
    if (!videoEl.paused) videoEl.pause();
    seek(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => { if (dragging) scrubSeek(e.clientX); };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragging) return;
    setDragging(false);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current = null;
    seek(e.clientX);
    if (wasPlayingRef.current) videoEl.play().catch(() => {});
  };

  const duration = durationOf();
  const pct = (t: number) => (duration > 0 ? Math.max(0, Math.min(100, (t / duration) * 100)) : 0);

  return (
    // The padded wrapper owns the pointer so the touch target is several times the track; the ratio math uses the track's rect.
    <div className="relative py-2 -my-1 cursor-pointer touch-none select-none" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
      <div ref={trackRef} className="relative overflow-hidden pointer-events-none rounded bg-white/10" style={{ height: TRACK_H }}>
        {duration > 0 && buffered.map((r, i) => (
          <div key={i} className="absolute top-0 h-full pointer-events-none bg-white/10" style={{ left: `${pct(r.start)}%`, width: `${Math.max(0, pct(r.end) - pct(r.start))}%` }} />
        ))}
        {peaks && (
          <>
            <canvas ref={waveBaseRef} className="absolute inset-0 w-full h-full pointer-events-none" />
            <canvas ref={waveBrightRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ clipPath: 'inset(0 100% 0 0)' }} />
          </>
        )}
        <div ref={fillRef} className="absolute top-0 left-0 h-full pointer-events-none bg-white/35" style={{ width: '0%' }} />
        {trailing && (
          <span className="absolute right-1.5 top-0 h-full flex items-center text-[10px] font-mono text-zinc-200 pointer-events-none">{trailing}</span>
        )}
        <div ref={playheadRef} className="absolute top-0 h-full w-px bg-white/90 pointer-events-none" style={{ left: '0%' }} />
      </div>
    </div>
  );
};
